import { useEffect } from 'react'
import { useNotifications } from '../queries/useNotifications.js'
import { useUIStore } from '../store/uiStore.js'
import { colorFor } from '../data/userColors.js'
import { timeAgo } from '../data/metaKeys.js'

/**
 * NotificationPanel — inbox dropdown anchored under the 🔔 bell.
 *
 * Lists every recent DM + @mention directed at the current user, newest
 * first. Unread items are highlighted blue. Clicking an item opens the
 * corresponding DM thread or comment panel.
 *
 * "Mark all read" flips every current item's seen-state to true (in
 * bulk, via the notifications hook).
 */
export default function NotificationPanel() {
  const open = useUIStore((s) => s.notifOpen)
  const close = useUIStore((s) => s.closeNotifs)
  const openDmWith = useUIStore((s) => s.openDmWith)
  const openCommentPanel = useUIStore((s) => s.openCommentPanel)
  const markMentionSeen = useUIStore((s) => s.markMentionSeen)
  const { items, unreadCount, markAllRead } = useNotifications()

  // Escape + outside-click to close
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    const onClick = (e) => {
      if (!e.target.closest('[data-notif-panel]') && !e.target.closest('[data-notif-bell]')) {
        close()
      }
    }
    window.addEventListener('keydown', onKey)
    // Delay so the bell click that opened us doesn't immediately close us
    const t = setTimeout(() => document.addEventListener('click', onClick), 0)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearTimeout(t)
      document.removeEventListener('click', onClick)
    }
  }, [open, close])

  if (!open) return null

  const handleClick = (item) => {
    if (item.kind === 'dm') {
      openDmWith(item.otherParticipants)
    } else if (item.kind === 'mention') {
      markMentionSeen(item.replyId)
      openCommentPanel({
        label: item.contextLabel,
        type: item.contextType || 'Context',
      })
    }
    close()
  }

  return (
    <div
      data-notif-panel
      style={{
        position: 'fixed',
        top: 54,
        right: 16,
        width: 360,
        maxWidth: '95vw',
        maxHeight: '70vh',
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#fafafa',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 800 }}>🔔 Inbox</span>
        <span
          style={{
            fontSize: 11,
            color: 'var(--muted)',
            fontWeight: 600,
          }}
        >
          {items.length === 0
            ? 'Nothing new'
            : unreadCount > 0
              ? `${unreadCount} unread`
              : 'All caught up'}
        </span>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--accent)',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
            }}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <div
            style={{
              padding: '40px 16px',
              textAlign: 'center',
              fontSize: 12,
              color: 'var(--muted-light)',
              fontStyle: 'italic',
            }}
          >
            No recent activity directed at you.
            <br />
            DMs and @mentions will show up here.
          </div>
        ) : (
          items.map((item) => (
            <NotifRow
              key={item.id}
              item={item}
              onClick={() => handleClick(item)}
            />
          ))
        )}
      </div>
    </div>
  )
}

/* ─── Row ─────────────────────────────────────────────────────────── */

function NotifRow({ item, onClick }) {
  const color = colorFor(item.author)
  const when = item.at ? timeAgo(new Date(item.at).getTime()) : ''
  const preview =
    (item.text || '').length > 90
      ? item.text.slice(0, 90) + '…'
      : item.text

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        width: '100%',
        padding: '10px 14px',
        background: item.seen ? '#fff' : '#eff6ff',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = item.seen ? '#f9fafb' : '#dbeafe')
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = item.seen ? '#fff' : '#eff6ff')
      }
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: color,
          color: '#fff',
          fontSize: 12,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {item.author?.[0] || '?'}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            color: 'var(--muted)',
            marginBottom: 2,
            fontWeight: 600,
          }}
        >
          {item.kind === 'dm' ? (
            <>
              💬 <strong style={{ color: 'var(--text)' }}>{item.author}</strong>
              {item.label !== item.author && (
                <span>in {item.label}</span>
              )}
            </>
          ) : (
            <>
              📣 <strong style={{ color: 'var(--text)' }}>{item.author}</strong>
              <span>mentioned you</span>
            </>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted-light)' }}>
            {when}
          </span>
        </div>
        {item.kind === 'mention' && item.contextLabel && (
          <div
            style={{
              fontSize: 10,
              color: 'var(--muted-light)',
              fontWeight: 600,
              marginBottom: 3,
            }}
          >
            on: {item.contextLabel}
          </div>
        )}
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {preview}
        </div>
      </div>
      {!item.seen && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: 'var(--accent)',
            flexShrink: 0,
            marginTop: 10,
          }}
        />
      )}
    </button>
  )
}
